import React from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { useCart, fmtMxn } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Tag, X, ShoppingCart, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Carrito() {
  const { cart, removeItem, setItemIadaAval, applyDiscount, removeDiscount, loading } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = React.useState("");
  const [applying, setApplying] = React.useState(false);

  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setApplying(true);
    await applyDiscount(code.trim().toUpperCase());
    setApplying(false);
  };

  const proceed = () => {
    if (!user) {
      toast.info("Inicia sesión para continuar con la compra");
      nav("/login?next=/carrito");
      return;
    }
    if (!cart?.items?.length) return;
    nav("/checkout");
  };

  const empty = !cart?.items?.length;

  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Carrito</p>
        <h1 className="mt-2 text-3xl font-bold text-[#0B0B0B] font-display">Tu selección</h1>

        {loading && <p className="mt-6 text-gray-500">Cargando...</p>}

        {!loading && empty && (
          <div className="mt-10 p-12 border-2 border-dashed border-gray-300 rounded-2xl text-center bg-[#F9FAFB]/40 animate-fade-in-up" data-testid="cart-empty">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F26A21]/10 border border-[#F26A21]/30 flex items-center justify-center">
              <ShoppingCart className="w-8 h-8 text-[#F26A21]" aria-hidden="true" />
            </div>
            <p className="mt-4 text-lg font-semibold text-[#0B0B0B]">Tu carrito está vacío</p>
            <p className="mt-1 text-sm text-gray-600 max-w-sm mx-auto">
              Explora los 193 programas del catálogo o diseña un programa a la medida con IA.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link
                to="/catalogo"
                data-testid="cart-empty-catalog-btn"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm transition-all hover:scale-[1.02]"
              >
                Ver catálogo <ArrowRight className="w-3 h-3" />
              </Link>
              <Link
                to="/tailor-made"
                data-testid="cart-empty-tailor-btn"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-gray-300 text-[#0B0B0B] hover:bg-black/5 font-semibold text-sm transition-all"
              >
                Diseñar tailor-made
              </Link>
            </div>
          </div>
        )}

        {!loading && !empty && (
          <div className="mt-8 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
              {cart.items.map((it) => {
                const snap = it.program_snapshot || {};
                const iadaEligible = !!snap.iada_aval_available;
                const withAval = !!it.with_iada_aval;
                return (
                  <article key={it.program_id}
                    data-testid={`cart-item-${snap.code}`}
                    className="p-4 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                          {snap.format} · {snap.code}
                        </p>
                        <p className="mt-1 font-semibold text-[#0B0B0B] truncate">{snap.title}</p>
                        {withAval && (
                          <span className="mt-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-[#F26A21] text-[#0B0B0B] uppercase tracking-widest font-bold"
                                data-testid={`cart-item-iada-badge-${snap.code}`}>
                            <Sparkles className="w-3 h-3" /> Con aval IADA · +{fmtMxn(it.iada_aval_amount_mxn)}
                          </span>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-mono font-semibold text-[#0B0B0B]">{fmtMxn(it.subtotal_item_mxn ?? it.unit_price_final_mxn)}</p>
                        {withAval && (
                          <p className="text-[10px] text-gray-500 mt-0.5">base {fmtMxn(it.base_price_mxn)}</p>
                        )}
                        <button onClick={() => removeItem(it.program_id)}
                          data-testid={`cart-remove-${snap.code}`}
                          aria-label="Quitar del carrito"
                          className="mt-1 inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
                          <Trash2 className="w-3 h-3" /> Quitar
                        </button>
                      </div>
                    </div>
                    {iadaEligible && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-[#0B0B0B]">Aval académico IADA</p>
                          <p className="text-[10px] text-gray-500">+30% sobre precio base · Universidad Anáhuac</p>
                        </div>
                        <label className="inline-flex items-center cursor-pointer" data-testid={`cart-iada-toggle-${snap.code}`}>
                          <input
                            type="checkbox"
                            checked={withAval}
                            onChange={(e) => setItemIadaAval(it.program_id, e.target.checked)}
                            className="sr-only peer"
                            data-testid={`cart-iada-toggle-input-${snap.code}`}
                          />
                          <span className="relative w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-[#F26A21] transition-colors">
                            <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                          </span>
                        </label>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 p-5 bg-[#F9FAFB] border border-gray-200 rounded-xl space-y-3" data-testid="cart-summary">
                <p className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">Resumen</p>
                <Row k="Subtotal" v={fmtMxn(cart.subtotal_mxn)} />
                {cart.discount_code ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-emerald-700 flex items-center gap-1">
                      <Tag className="w-3 h-3" /> {cart.discount_code}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-700 font-mono">-{fmtMxn(cart.discount_mxn)}</span>
                      <button onClick={removeDiscount} aria-label="Quitar descuento" className="text-red-500 hover:text-red-700">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={apply} className="flex gap-2">
                    <input value={code} onChange={(e) => setCode(e.target.value)}
                      placeholder="Código de descuento"
                      data-testid="cart-discount-input"
                      className="flex-1 min-w-0 bg-white border border-gray-300 rounded-md px-2 py-1.5 text-xs text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
                    <button type="submit" disabled={applying} data-testid="cart-discount-apply"
                      className="text-xs px-3 py-1.5 rounded bg-[#0B0B0B] text-white hover:bg-black font-semibold disabled:opacity-40">
                      Aplicar
                    </button>
                  </form>
                )}
                <Row k="IVA 16%" v={fmtMxn(cart.tax_mxn)} />
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">Total</span>
                    <span className="font-mono text-2xl font-bold text-[#F26A21]" data-testid="cart-total">{fmtMxn(cart.total_mxn)}</span>
                  </div>
                </div>
                <button onClick={proceed} data-testid="cart-checkout-btn"
                  className="w-full py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold inline-flex items-center justify-center gap-1">
                  Proceder al pago <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-[10px] text-gray-500 text-center">IVA incluido. Pago seguro.</p>
              </div>
            </aside>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600">{k}</span>
      <span className="text-[#0B0B0B] font-mono">{v}</span>
    </div>
  );
}
